import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CachedProduct, CachedProductUnit } from "./db";

export interface CartLine {
  key: string;
  product: CachedProduct;
  unit: CachedProductUnit | null;
  quantity: number;
  multiplier: number;
  unitPrice: number;
}

interface CartContextValue {
  lines: CartLine[];
  total: number;
  add(product: CachedProduct, unit?: CachedProductUnit | null): void;
  decrement(key: string): void;
  setQuantity(key: string, quantity: number): void;
  clear(): void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function cartLineKey(
  productId: string,
  unitId?: string | null,
): string {
  return `${productId}:${unitId ?? "base"}`;
}

export function unitPriceFor(
  product: CachedProduct,
  unit?: CachedProductUnit | null,
): number {
  if (!unit) return product.salePrice;
  return unit.salePrice ?? Math.round(product.salePrice * unit.multiplier);
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>([]);

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      total: lines.reduce(
        (sum, line) =>
          sum + Math.round(line.unitPrice * line.quantity),
        0,
      ),
      add(product, unit = null) {
        const key = cartLineKey(product.id, unit?.id);
        const multiplier = unit?.multiplier ?? 1;
        const unitPrice = unitPriceFor(product, unit);

        setLines((current) => {
          const found = current.find((line) => line.key === key);
          if (found) {
            return current.map((line) =>
              line.key === key
                ? { ...line, quantity: line.quantity + 1 }
                : line,
            );
          }

          return [
            ...current,
            {
              key,
              product,
              unit,
              quantity: 1,
              multiplier,
              unitPrice,
            },
          ];
        });
      },
      decrement(key) {
        setLines((current) =>
          current
            .map((line) =>
              line.key === key
                ? { ...line, quantity: line.quantity - 1 }
                : line,
            )
            .filter((line) => line.quantity > 0),
        );
      },
      setQuantity(key, quantity) {
        setLines((current) =>
          current
            .map((line) =>
              line.key === key
                ? { ...line, quantity: Math.max(0, quantity) }
                : line,
            )
            .filter((line) => line.quantity > 0),
        );
      },
      clear() {
        setLines([]);
      },
    }),
    [lines],
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return value;
}
